import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Minus, Plus } from "lucide-react";

const DAY_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

function changeDate(date, direction) {
  const dt = new Date(date);
  dt.setDate(dt.getDate() + direction);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function todayStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function useDayName(date) {
  const { t } = useTranslation();
  return useMemo(() => {
    if (!date) return "";
    const dt = new Date(date + "T00:00:00");
    const dayIndex = dt.getDay();
    return t("common.days_" + DAY_KEYS[dayIndex]);
  }, [date, t]);
}

export default function DatePicker({ value, onChange, min, className = "" }) {
  const dayName = useDayName(value);

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => onChange(changeDate(value, -1))}
        className="border border-border p-3 hover:border-foreground/40 transition-colors shrink-0"
      >
        <Minus className="h-4 w-4 text-muted-foreground/80" />
      </button>
      <div className="flex-1 relative">
        <input
          type="date"
          value={value}
          min={min}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full bg-card border border-border p-3 text-sm focus:border-volt focus:outline-none pl-20 sm:pl-28 ${className}`}
        />
        {value && dayName && (
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <span className="text-[9px] sm:text-xs text-volt font-bold uppercase tracking-wider">
              {dayName}
            </span>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => onChange(changeDate(value, 1))}
        className="border border-border p-3 hover:border-foreground/40 transition-colors shrink-0"
      >
        <Plus className="h-4 w-4 text-muted-foreground/80" />
      </button>
    </div>
  );
}

export function DatePickerCompact({ value, onChange, min, className = "" }) {
  const dayName = useDayName(value);

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => onChange(changeDate(value, -1))}
        className="border border-border px-2 py-1.5 hover:border-foreground/40 transition-colors shrink-0"
      >
        <Minus className="h-3 w-3 text-muted-foreground/80" />
      </button>
      <div className="relative">
        <input
          type="date"
          value={value}
          min={min}
          onChange={(e) => onChange(e.target.value)}
          className={`bg-card border border-border px-2 py-1.5 text-[10px] tracking-widest uppercase text-muted-foreground font-bold focus:outline-none focus:border-volt pl-14 sm:pl-16 ${className}`}
        />
        {value && dayName && (
          <div className="absolute inset-y-0 left-2 flex items-center pointer-events-none">
            <span className="text-[7px] sm:text-[8px] text-volt font-bold tracking-wider">
              {dayName}
            </span>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => onChange(changeDate(value, 1))}
        className="border border-border px-2 py-1.5 hover:border-foreground/40 transition-colors shrink-0"
      >
        <Plus className="h-3 w-3 text-muted-foreground/80" />
      </button>
    </div>
  );
}
